import {
    AfterViewChecked,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    OnChanges,
    Output,
    SimpleChanges,
} from '@angular/core';
import { LedgerRow, LedgerTitleRow } from './account-ledger.types';

const TITLE_LABEL_KEYS: Record<LedgerTitleRow['variant'], string> = {
    opening: 'abcp_ledger_row_opening',
    closing: 'abcp_ledger_row_closing',
};

@Component({
    selector: 'abcp-account-ledger',
    templateUrl: './account-ledger.component.html',
    styleUrls: ['./account-ledger.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AbcpAccountLedgerComponent implements OnChanges, AfterViewChecked {
    @Input() rows: LedgerRow[] = [];
    @Input() selectedRowId: string | null = null;
    /** REV-22352 — the line a comment is being written for: drawn like a marking in progress. */
    @Input() pendingRowId: string | null = null;
    @Input() loading = false;
    @Input() error = false;
    /** More rows exist on the server — the closing row follows only once the last page is in. */
    @Input() canLoadMore = false;
    @Input() loadingMore = false;

    /** A click on a booking line, or on a balance line with a comment. */
    @Output() rowSelect = new EventEmitter<string>();
    /** «Kommentieren» on the selected booking line. */
    @Output() markRequest = new EventEmitter<string>();
    /** «Pendenz erfassen» on the selected booking line. */
    @Output() markTodoRequest = new EventEmitter<string>();
    @Output() loadMore = new EventEmitter<void>();

    readonly columnCount = 8;
    readonly skeletonRows = [1, 2, 3, 4, 5, 6];

    /** Set until the selected line is rendered: it may arrive with a later page. */
    private revealPending = false;

    constructor(private readonly host: ElementRef<HTMLElement>) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['selectedRowId']) {
            this.revealPending = this.selectedRowId !== null;
        }
    }

    ngAfterViewChecked(): void {
        if (!this.revealPending || this.selectedRowId === null) {
            return;
        }
        const line = this.host.nativeElement.querySelector(`[data-row-id="${CSS.escape(this.selectedRowId)}"]`);
        if (line !== null) {
            line.scrollIntoView({ block: 'nearest' });
            this.revealPending = false;
        }
    }

    get isEmpty(): boolean {
        return this.rows.length === 0;
    }

    /**
     * Placeholders only while there is nothing to show yet. A reload keeps the rows it has until the new
     * ones arrive: placeholders over old rows read as two states at once, and swapping the rows for
     * placeholders on every refresh (a poll, a reload after a write) makes the table flash.
     */
    get showSkeleton(): boolean {
        return this.loading && this.rows.length === 0;
    }

    trackByRowId(_index: number, row: LedgerRow): string {
        return row.id;
    }

    titleLabelKey(variant: LedgerTitleRow['variant']): string {
        return TITLE_LABEL_KEYS[variant];
    }

    isSelected(row: LedgerRow): boolean {
        return row.id === this.selectedRowId;
    }

    onRowClick(row: LedgerRow): void {
        if (row.kind === 'booking' || row.hasComment) {
            this.rowSelect.emit(row.id);
        }
    }

    onMarkClick(row: LedgerRow): void {
        this.markRequest.emit(row.id);
    }

    onMarkTodoClick(row: LedgerRow): void {
        this.markTodoRequest.emit(row.id);
    }

    /** A title row gets a second line when it has an account-currency figure — or must say it has none. */
    hasForeignLine(row: LedgerRow): boolean {
        return row.kind === 'title' ? !!row.foreignBalance || !!row.foreignUnavailable : !!row.foreign;
    }

    onLoadMoreClick(): void {
        if (this.loadingMore) {
            return;
        }
        this.loadMore.emit();
    }
}
